import { Money } from './cardDetailHeroDemoData'

/**
 * Display helpers shared by the two hero variants. The full hero and the pinned
 * compact bar show the same cardholder and the same available/limit figures, so
 * these must not be forked between them — the same reasoning that lifted the
 * status pill into `StatusPill`.
 */

/**
 * The contract carries `cardholder.displayName` and no initials field, so the
 * avatar fallback is derived. Deliberately dumb — first letter of the first two
 * whitespace-separated tokens — rather than trying to be clever about particles,
 * suffixes, or multi-word surnames.
 */
export const initialsFrom = (displayName: string): string =>
  displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

/**
 * The number of minor-unit decimal places for a currency (2 for USD, 0 for JPY,
 * 3 for BHD). This is a per-currency constant, but reading it means building an
 * `Intl.NumberFormat` — costly to reconstruct every render for each amount — so
 * results are memoized by currency code. Falls back to 2 for any code Intl can't
 * resolve.
 */
const fractionDigitsCache = new Map<string, number>()
export const currencyFractionDigits = (currency: string): number => {
  const cached = fractionDigitsCache.get(currency)
  if (cached !== undefined) return cached
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    digits = 2
  }
  fractionDigitsCache.set(currency, digits)
  return digits
}

/**
 * Minor units → major units at the display edge. The scale is currency-aware:
 * most currencies are 2-decimal (÷100), but zero-decimal (JPY) and three-decimal
 * (BHD) currencies are not — a hardcoded ÷100 would misprice them. (Shared by
 * both hero variants, so the compact bar gets the same currency handling as the
 * full summary.)
 */
export const toDisplayAmount = (money: Money): number =>
  money.amountMinor / 10 ** currencyFractionDigits(money.currency)
